import type {ReactElement} from 'react';
import React from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import {openPosterEditorItemPositionModal} from '@Modals/poster-editor-item-position-modal/poster-editor-item-position-modal';
import {
  deleteItemByID,
  duplicateActiveItems,
  onCopyStylesClicked,
  showCopyStylesAsNewFeature,
  showEditorNotificationMessageGrowl,
  toggleLockStateForSelectedItem,
} from '@Components/poster-editor/library/poster-editor-library';
import {openPosterEditorItemOpacityModal} from '@Modals/poster-editor-item-opacity-modal/poster-editor-item-opacity-modal';
import {openPosterEditorItemFontSizeModal} from '@Modals/poster-editor-item-font-size-modal/poster-editor-item-font-size-modal';
import {openPosterEditorItemFontStyleModal} from '@Modals/poster-editor-item-font-style-modal/poster-editor-item-font-style-modal';
import {openPosterEditorSpellcheckModal} from '@Modals/poster-editor-item-spellcheck-modal';
import {openPosterEditorHyperlinkModal} from '@Modals/poster-editor-item-hyperlink-modal';
import {openPosterEditorAlignmentModal} from '@Modals/poster-editor-item-alignment-modal';
import {openPosterEditorShadowModal} from '@Modals/poster-editor-item-shadow-modal';
import {openPosterEditorItemBackgroundPanel} from '@Modals/poster-editor-item-background-modal';
import {openPosterEditorTextOutlineModal} from '@Modals/poster-editor-text-outline-modal';
import {openPosterEditorTextFillModal} from '@Modals/poster-editor-text-fill-modal';
import {openPosterEditorTextItemFontModal} from '@Modals/poster-editor-text-item-font-modal';
import {isUserPremium} from '@Libraries/user.library';
import {openPosterEditorItemListModal} from '@Modals/poster-editor-item-list-modal';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {useEditorPrefetchTextMobileModules} from '@Hooks/poster-editor/useEditorPrefetchTextMobileModules';
import {openPosterEditorEditTextModal} from '@Modals/poster-editor-edit-text-modal';
import {openPosterEditorItemLetterSpacingModal} from '@Modals/poster-editor-item-letter-spacing-modal';
import {openPosterEditorLineHeightModal} from '@Modals/poster-editor-item-line-height-modal';
import {TextItemBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import {CopyStyleState} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {useAppSelector} from '@/hooks';
import {getCopiedStyles} from '@Components/poster-editor/poster-editor-reducer';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {useGetKeyFromTextStylesActiveItem} from '@Hooks/poster-editor/useGetKeyFromTextStylesActiveItem';
import {ItemColorFill} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/item-color-fill/item-color-fill';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import styles from './text-bottom-bar.module.scss';
import {openItemAnimationModal} from '@Modals/poster-editor-item-animation-modal';
import {noop} from '@Utils/general.util';

export function TextBottomBar(): ReactElement<any> | null {
  const copiedStylesState: CopyStyleState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const isLocked = useGetKeyFromActiveItem('lockMovementX');
  const isBold = useGetKeyFromTextStylesActiveItem('isBold');

  useEditorPrefetchTextMobileModules();

  if (!activeItem?.isText()) {
    return null;
  }

  const onClickSpellcheck = (): void => {
    if (isUserPremium()) {
      openPosterEditorSpellcheckModal();
    } else {
      window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_SPELL_CHECK);
    }
  };

  const showDisabledOutlineMessageGrowl = (): void => {
    showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_disabled_text_outline_mobile_text'));
  };

  const pulsatingDotForCopyStyles = (): ReactElement<any> | null => {
    if (showCopyStylesAsNewFeature()) {
      return <PulsatingDot containerClassname={`_position-absolute  ${styles.pulsatingDotForCopyStyles}`} />;
    }
    return null;
  };

  const onClickHandler = (itemId: TextItemBottomBarOptions): void => {
    switch (itemId) {
      case TextItemBottomBarOptions.EDIT:
        break;
      case TextItemBottomBarOptions.TEXT:
        openPosterEditorEditTextModal();
        break;
      case TextItemBottomBarOptions.POSITION:
        openPosterEditorItemPositionModal();
        break;
      case TextItemBottomBarOptions.DELETE:
        deleteItemByID(activeItem.uid);
        break;
      case TextItemBottomBarOptions.LOCK:
        toggleLockStateForSelectedItem();
        break;
      case TextItemBottomBarOptions.OPACTIY:
        openPosterEditorItemOpacityModal();
        break;
      case TextItemBottomBarOptions.DUPLICATE:
        duplicateActiveItems();
        break;
      case TextItemBottomBarOptions.SIZE:
        openPosterEditorItemFontSizeModal();
        break;
      case TextItemBottomBarOptions.STYLE:
        openPosterEditorItemFontStyleModal();
        break;
      case TextItemBottomBarOptions.LETTER_SPACING:
        openPosterEditorItemLetterSpacingModal();
        break;
      case TextItemBottomBarOptions.LINE_HEIGHT:
        openPosterEditorLineHeightModal();
        break;
      case TextItemBottomBarOptions.FONT:
        openPosterEditorTextItemFontModal();
        break;
      case TextItemBottomBarOptions.LISTS:
        openPosterEditorItemListModal();
        break;
      case TextItemBottomBarOptions.SPELLCHECK:
        onClickSpellcheck();
        break;
      case TextItemBottomBarOptions.COPY_STYLES:
        onCopyStylesClicked();
        break;
      case TextItemBottomBarOptions.HYPERLINK:
        openPosterEditorHyperlinkModal();
        break;
      case TextItemBottomBarOptions.ALIGNMENT:
        openPosterEditorAlignmentModal();
        break;
      case TextItemBottomBarOptions.SHADOW:
        openPosterEditorShadowModal();
        break;
      case TextItemBottomBarOptions.ANIMATION:
        openItemAnimationModal();
        break;
      case TextItemBottomBarOptions.FILL:
        openPosterEditorTextFillModal();
        break;
      case TextItemBottomBarOptions.BACKGROUND:
        openPosterEditorItemBackgroundPanel();
        break;
      case TextItemBottomBarOptions.OUTLINE:
        if (isBold) {
          showDisabledOutlineMessageGrowl();
          return;
        }
        openPosterEditorTextOutlineModal();
        break;
      default:
    }
  };

  const getCustomFillContent = (): ReactElement<any> => {
    return <ItemColorFill />;
  };

  return (
    <BottomBar
      id="poster-editing-default-bottom-bar"
      onClick={onClickHandler}
      items={[
        {
          id: TextItemBottomBarOptions.EDIT,
          icon: 'icon-pencil',
          text: window.i18next.t('pmwjs_edit_item'),
          onClick: (e): void => {
            if (activeItem?.isText()) {
              // @ts-ignore
              activeItem?.enterEditMode(e);
            }
          },
        },
        {
          id: TextItemBottomBarOptions.TEXT,
          icon: 'icon-text',
          text: window.i18next.t('pmwjs_text'),
        },
        {
          id: TextItemBottomBarOptions.FONT,
          icon: 'icon-font',
          text: window.i18next.t('pmwjs_font'),
        },
        {
          id: TextItemBottomBarOptions.SIZE,
          icon: 'icon-text-size',
          text: window.i18next.t('pmwjs_size'),
        },
        {
          id: TextItemBottomBarOptions.FILL,
          customContent: getCustomFillContent(),
          text: window.i18next.t('pmwjs_fill'),
        },
        {
          id: TextItemBottomBarOptions.STYLE,
          icon: 'icon-text-style',
          text: window.i18next.t('pmwjs_style'),
        },
        {
          id: TextItemBottomBarOptions.DUPLICATE,
          icon: 'icon-copy',
          text: window.i18next.t('pmwjs_duplicate'),
        },
        {
          id: TextItemBottomBarOptions.POSITION,
          icon: 'icon-prints-stack',
          text: window.i18next.t('pmwjs_position'),
        },
        {
          id: TextItemBottomBarOptions.OUTLINE,
          icon: 'icon-outline',
          text: window.i18next.t('pmwjs_outline'),
          isDisabled: activeItem.isOutlineDisabled(),
        },
        {
          id: TextItemBottomBarOptions.ALIGNMENT,
          icon: 'icon-align-center',
          text: window.i18next.t('pmwjs_alignment'),
        },
        {
          id: TextItemBottomBarOptions.LISTS,
          icon: 'icon-list-ordered',
          text: window.i18next.t('pmwjs_lists'),
        },
        {
          id: TextItemBottomBarOptions.LINE_HEIGHT,
          icon: 'icon-line-height',
          text: window.i18next.t('pmwjs_line_height'),
        },
        {
          id: TextItemBottomBarOptions.LETTER_SPACING,
          icon: 'icon-letter-spacing',
          text: window.i18next.t('pmwjs_letter_spacing'),
        },
        {
          id: TextItemBottomBarOptions.BACKGROUND,
          icon: 'icon-background',
          text: window.i18next.t('pmwjs_background'),
        },
        {
          id: TextItemBottomBarOptions.SHADOW,
          icon: 'icon-shadow',
          text: window.i18next.t('pmwjs_shadow'),
        },
        {
          id: TextItemBottomBarOptions.SPELLCHECK,
          icon: 'icon-spellcheck',
          text: window.i18next.t('pmwjs_spellcheck'),
          tag: !isUserPremium() ? <Icon className="color-premium" icon="icon-crown" size={IconSize.SIZE_ICON_20} /> : undefined,
        },
        {
          id: TextItemBottomBarOptions.COPY_STYLES,
          icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
          text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_paste_style'),
          isSelected: copiedStylesState === CopyStyleState.COPIED,
          tag: pulsatingDotForCopyStyles(),
        },
        {
          id: TextItemBottomBarOptions.ANIMATION,
          icon: 'icon-animation',
          text: window.i18next.t('pmwjs_animation'),
        },
        {
          id: TextItemBottomBarOptions.HYPERLINK,
          icon: 'icon-link',
          text: window.i18next.t('pmwjs_hyperlink'),
        },
        {
          id: TextItemBottomBarOptions.OPACTIY,
          icon: 'icon-opacity',
          text: window.i18next.t('pmwjs_opacity'),
        },
        {
          id: TextItemBottomBarOptions.LOCK,
          icon: isLocked ? 'icon-lock' : 'icon-unlock',
          text: isLocked ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
        },
        {
          id: TextItemBottomBarOptions.DELETE,
          icon: 'icon-delete',
          text: window.i18next.t('pmwjs_delete'),
          className: 'color-danger-100',
        },
      ]}
    />
  );
}
